/**
 * Collection Tabs — horizontal panels with optional Swiper (per-tab block setting).
 */

class CollectionProductTabs extends HTMLElement {
  connectedCallback() {
    /** @type {Map<HTMLElement, unknown>} */
    this._swipers = new Map();
    this.refreshElements();

    if (!this._tabs.length || !this._panels.length) return;

    this._onTabClick = this._onTabClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    this._tabs.forEach((btn) => btn.addEventListener('click', this._onTabClick));
    this._tabs.forEach((btn) => btn.addEventListener('keydown', this._onKeydown));

    const activeIndex = [...this._tabs].findIndex(
      (t) => t.getAttribute('aria-selected') === 'true'
    );

    const initial = activeIndex >= 0 ? activeIndex : 0;
    this.activateTab(initial, { scroll: false });

    this._panels.forEach((panel) => {
      if (panel.dataset.productLayout === 'slider') {
        this.refreshPanelSwiper(panel);
      }
    });
  }

  refreshElements() {
    this._tabs = this.querySelectorAll('[data-collection-tab]');
    this._panels = this.querySelectorAll('[data-collection-panel]');
  }

  disconnectedCallback() {
    if (this._swipers) {
      this.destroyAllSwipers();
    }

    if (!this._tabs || !this._onTabClick) return;

    this._tabs.forEach((btn) => btn.removeEventListener('click', this._onTabClick));
    this._tabs.forEach((btn) => btn.removeEventListener('keydown', this._onKeydown));
  }

  destroyAllSwipers() {
    if (!this._swipers) return;
    this._swipers.forEach((instance, swiperEl) => {
      /** @type {(() => void) | undefined} */
      const onResize =
        swiperEl instanceof HTMLElement ? swiperEl.__ctsMiddleNavThumbResize : undefined;
      if (typeof onResize === 'function') {
        window.removeEventListener('resize', onResize);
        if (swiperEl instanceof HTMLElement) delete swiperEl.__ctsMiddleNavThumbResize;
      }
      try {
        if (instance && typeof instance.destroy === 'function') {
          instance.destroy(true, true);
        }
      } catch {
        /* ignore */
      }
    });
    this._swipers.clear();
  }

  _cols() {
    let xl = parseInt(String(this.dataset.showXl || '4'), 10);
    let sm = parseInt(String(this.dataset.showSm || '2'), 10);
    if (Number.isNaN(xl) || xl < 1) xl = 4;
    if (Number.isNaN(sm) || sm < 1) sm = 2;
    const tablet = xl >= 3 ? 3 : Math.max(xl, sm);
    return { xl, sm, tablet };
  }

  /**
   * Mirrors featured-collection `theme.collectionSlider` — vertical-center arrows beside slides.
   * @param {HTMLElement} swiperEl
   * @param {HTMLElement} panel
   */
  _bindMiddleNavThumbOffset(swiperEl, panel) {
    if (!(swiperEl instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
    if (String(this.dataset.sliderNavPosition || '') !== 'middle') return;

    const root = panel.querySelector('[data-collection-tabs-slider-root]');
    if (!(root instanceof HTMLElement)) return;

    /** @returns {void} */
    const run = () => {
      const thumbs = swiperEl.querySelectorAll('.card--client-height');
      const first = thumbs[0];
      const px = first instanceof HTMLElement ? first.clientHeight : 0;
      root.style.setProperty('--slider-navigation-top-offset', px > 0 ? `${px / 2}px` : '50%');
    };

    /** @type {(() => void) | undefined} */
    const prev = swiperEl.__ctsMiddleNavThumbResize;
    if (typeof prev === 'function') window.removeEventListener('resize', prev);
    swiperEl.__ctsMiddleNavThumbResize = run;
    window.addEventListener('resize', run);
    run();
  }

  refreshPanelSwiper(panel) {
    if (!panel || panel.dataset.productLayout !== 'slider') return;
    if (typeof window.Swiper !== 'function') return;

    const swiperEl = panel.querySelector('[data-collection-tab-swiper]');
    if (!swiperEl) return;

    const existing = swiperEl.swiper;
    if (existing) {
      window.requestAnimationFrame(() => {
        try {
          existing.update();
        } catch {
          /* ignore */
        }
        /** @type {(() => void) | undefined} */
        const rh = swiperEl.__ctsMiddleNavThumbResize;
        if (typeof rh === 'function') rh();
      });
      return;
    }

    const { xl, sm, tablet } = this._cols();
    const showNav = String(this.dataset.sliderNavigation || 'true') === 'true';
    const navRoot = panel.querySelector('[data-collection-tab-slider-nav]');
    let prev = null;
    let next = null;
    if (showNav && navRoot) {
      prev = navRoot.querySelector('.swiper-button-prev');
      next = navRoot.querySelector('.swiper-button-next');
    }

    const rtl = document.documentElement.getAttribute('dir') === 'rtl';

    const showPagination = String(this.dataset.sliderPagination || 'false') === 'true';
    const paginationType = this.dataset.sliderPaginationType || 'bullets';
    let paginationConfig = false;
    if (showPagination) {
      const paginationEl = panel.querySelector('.swiper-pagination');
      if (paginationEl) {
        if (paginationType === 'progress-bar') {
          paginationConfig = {
            el: paginationEl,
            type: 'progressbar',
          };
        } else {
          paginationConfig = {
            el: paginationEl,
            clickable: true,
            type: paginationType === 'counter' ? 'fraction' : 'bullets',
          };
        }
      }
    }

    const sliderAutoplay = String(this.dataset.sliderAutoplay || 'false') === 'true';
    const autoPlayTime = parseInt(this.dataset.sliderAutoplayTime || '3000', 10);
    let autoplayConfig = false;
    if (sliderAutoplay) {
      autoplayConfig = {
        delay: autoPlayTime,
        pauseOnMouseEnter: true,
      };
    }

    try {
      const instance = new window.Swiper(swiperEl, {
        slidesPerView: sm,
        spaceBetween: 20,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        loop: false,
        rtl,
        autoplay: autoplayConfig,
        pagination: paginationConfig,
        navigation:
          prev && next
            ? {
                prevEl: prev,
                nextEl: next,
              }
            : undefined,
        breakpoints: {
          0: {
            slidesPerView: sm,
          },
          750: {
            slidesPerView: tablet,
          },
          992: {
            slidesPerView: xl,
          },
        },
      });
      this._swipers.set(swiperEl, instance);
      this._bindMiddleNavThumbOffset(swiperEl, panel);
    } catch {
      /* ignore */
    }
  }

  refreshActiveSwiper() {
    const active = [...this._panels].find((p) =>
      p.classList.contains('is-active')
    );
    if (!active) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.refreshPanelSwiper(active);
      });
    });
  }

  _replayPanelAnimations(panel) {
    const OFFSCREEN = 'scroll-trigger--offscreen';
    const triggers = panel.querySelectorAll('.scroll-trigger');
    if (!triggers.length) return;
    triggers.forEach((el, i) => {
      el.classList.add(OFFSCREEN);
      el.style.animationDelay = `${i * 40}ms`;
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        triggers.forEach((el) => el.classList.remove(OFFSCREEN));
      });
    });
  }

  _scrollTabChipIntoView(index) {
    const clamped = this._clampTabIndex(index);
    const strips = this.querySelectorAll('.collection-product-tabs__tab');
    /** @type {HTMLElement | null} */
    const row = strips[clamped] instanceof HTMLElement ? strips[clamped] : null;
    if (!row) return;

    try {
      const reduce =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: reduce ? 'auto' : 'smooth',
      });
    } catch {
      /* ignore */
    }
  }

  _clampTabIndex(index) {
    if (!this._panels?.length) return 0;
    return Math.min(Math.max(Number(index) || 0, 0), this._panels.length - 1);
  }

  activateTab(rawIndex, { scroll = true } = {}) {
    this.refreshElements();
    if (!this._tabs.length || !this._panels.length) return;

    const index = this._clampTabIndex(rawIndex);

    this._panels.forEach((panel, i) => {
      const active = i === index;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');

      /** @type {HTMLElement | null} */
      const tab = /** @type {HTMLElement | null} */ (this._tabs[i] || null);
      if (tab) {
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      }
    });

    if (scroll) {
      this._scrollTabChipIntoView(index);
      this._replayPanelAnimations(this._panels[index]);
    }
    this.refreshActiveSwiper();

    this._panels.forEach((panel, i) => {
      if (i !== index && panel.dataset.productLayout === 'slider') {
        const swiperEl = panel.querySelector('[data-collection-tab-swiper]');
        const inst = swiperEl && swiperEl.swiper;
        if (inst) {
          try {
            inst.update();
          } catch {
            /* ignore */
          }
        }
      }
    });
  }

  _onTabClick(event) {
    const btn = event.currentTarget;
    if (!(btn instanceof HTMLElement)) return;
    const index = [...this._tabs].indexOf(btn);
    if (index < 0) return;
    this.activateTab(index);
    btn.focus();
  }

  _onKeydown(event) {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const btn = event.currentTarget;
    const current = [...this._tabs].indexOf(btn);
    if (current < 0) return;

    event.preventDefault();
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    let next = current;

    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this._tabs.length - 1;
    else if (event.key === 'ArrowRight') next = rtl ? current - 1 : current + 1;
    else if (event.key === 'ArrowLeft') next = rtl ? current + 1 : current - 1;

    if (next < 0) next = this._tabs.length - 1;
    if (next >= this._tabs.length) next = 0;

    const target = this._tabs[next];
    if (!target) return;
    target.focus();
    target.click();
  }
}

if (!customElements.get('collection-product-tabs')) {
  customElements.define('collection-product-tabs', CollectionProductTabs);
}

/** Theme editor — switch visible tab panel when the corresponding block is selected. Mirrors collection-tabs-banner + media-showcase patterns. */
(function bindCollectionTabsEditorSupport() {
  if (typeof document === 'undefined') return;

  /** @type {HTMLElement | null} */
  function resolveCollectionTabsInstance(target) {
    if (!(target instanceof HTMLElement)) return null;
    /** @type {HTMLElement | null} */
    const root = /** @type {HTMLElement | null} */ (target.closest?.('collection-product-tabs'));
    return root instanceof HTMLElement ? root : null;
  }

  /**
   * @param {HTMLElement} cmp
   * @param {{ sectionId?: string; blockId?: string } | undefined} detail
   * @param {EventTarget | null} evtTarget
   */
  function resolveTabIndex(cmp, detail, evtTarget) {
    if (!(evtTarget instanceof HTMLElement)) return NaN;

    if (detail && typeof detail.blockId === 'string') {
      /** @type {HTMLElement | undefined} */
      let matchedPanel;
      cmp.querySelectorAll('[data-collection-panel]').forEach((pane) => {
        if (!(pane instanceof HTMLElement)) return;
        if (pane.dataset.blockId === detail.blockId && pane.dataset.tabIndex != null) {
          matchedPanel = pane;
        }
      });
      if (matchedPanel != null && matchedPanel.dataset.tabIndex != null) {
        const n = parseInt(String(matchedPanel.dataset.tabIndex), 10);
        return Number.isNaN(n) ? NaN : n;
      }
    }

    const tabRow = evtTarget.closest('.collection-product-tabs__tab');
    if (tabRow && tabRow.dataset.tabIndex != null) {
      const n = parseInt(String(tabRow.dataset.tabIndex), 10);
      return Number.isNaN(n) ? NaN : n;
    }

    const panel = evtTarget.closest('[data-collection-panel]');
    if (panel && panel.dataset.tabIndex != null) {
      const n = parseInt(String(panel.dataset.tabIndex), 10);
      return Number.isNaN(n) ? NaN : n;
    }

    return NaN;
  }

  document.addEventListener('shopify:block:select', function (/** @type {CustomEvent<{ sectionId?: string; blockId?: string }>} */ event) {
    if (!(window.Shopify && window.Shopify.designMode)) return;

    const tgt = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    if (!tgt || !event.detail) return;

    /** @type {HTMLElement | null} */
    const cmp = resolveCollectionTabsInstance(tgt);
    if (!cmp || typeof cmp.dataset.sectionId !== 'string') return;

    if (
      typeof event.detail.sectionId !== 'undefined' &&
      String(event.detail.sectionId) !== String(cmp.dataset.sectionId)
    ) {
      return;
    }

    const idx = resolveTabIndex(cmp, event.detail, event.target instanceof HTMLElement ? event.target : null);
    if (!Number.isNaN(idx) && typeof cmp.activateTab === 'function') {
      cmp.activateTab(idx);
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (!(window.Shopify && window.Shopify.designMode)) return;

    const loadedSection = event.target instanceof HTMLElement ? event.target : null;
    if (!loadedSection) return;

    requestAnimationFrame(function () {
      loadedSection.querySelectorAll('collection-product-tabs').forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        /** @type {HTMLElement & { refreshElements?: () => void; activateTab?: (i: number) => void }} */
        const el = /** @type {HTMLElement & { refreshElements?: () => void; activateTab?: (i: number) => void }} */ (node);
        el.refreshElements?.();
        const activeIndex = [...(el.querySelectorAll('[data-collection-tab]') ?? [])].findIndex(
          (btn) =>
            btn instanceof HTMLElement && btn.getAttribute('aria-selected') === 'true'
        );
        const initial = activeIndex >= 0 ? activeIndex : 0;
        el.activateTab?.(initial);
      });
    });
  });
})();
